import { Add, Delete, Edit, FilterAlt, Refresh } from "@mui/icons-material";
import { Grid, IconButton, Typography } from "@mui/material";
import DataGrid from '../../../../components/DataGrid';
import ContentTitle from "../../../../components/Content/ContentTitle";
import { useLanguageContext } from "../../../../contexts/LanguageContext";
import { useEffect, useCallback } from "react";
import FormHelper from "../../../../helpers/FormHelper";
import AddEditDialog from "./AddEditDialog";
import { useAuthContext } from "../../../../contexts/AuthContext";
import RemoveDialog from "./RemoveDialog";
import { INavigationLink } from "../../../../interfaces/INavigation";
import Filters from "./Filters";
import { RegistrationStatusEnum } from "../../../../data/Enums";
import Tooltip from "../../../../components/Tooltip";
import useDrawer from "../../../../hooks/useDrawer";
import useDialog from "../../../../hooks/useDialog";
import PartnerPmsService from "../../../../services/PartnerPmsService";
import { IRoomBlocking } from "../../../../interfaces/IPartnerPms";
import DateTimeHelper from "../../../../helpers/DateTimeHelper";
import useState from "../../../../hooks/useState";


interface IStateValues {
    filters: FormData | undefined;
    data: IRoomBlocking[];
    selectedRow: IRoomBlocking | undefined;
}

const RoomBlocking = (linkProps: INavigationLink) => {
    const { translate } = useLanguageContext();
    const { hasPermission } = useAuthContext();

    const state = useState<IStateValues>({
        filters: undefined,
        data: [],
        selectedRow: undefined
    });

    const filtersDrawer = useDrawer();
    const addEditDialog = useDialog();
    const removeDialog = useDialog();


    useEffect(() => {
        handleRefresh();
        // eslint-disable-next-line
    }, [state.values.filters]);
    
    
    const handleRefresh = useCallback(() => {
        let formData = state.values.filters;
        
        if (formData === undefined) {
            formData = FormHelper.Create(undefined);

            const now = DateTimeHelper.Now();
            formData = FormHelper.Add(formData, 'startDate', now);
            formData = FormHelper.Add(formData, 'endDate', now);
        }

        PartnerPmsService.ListRoomBlocking(formData)
            .then((resp) => {
                state.update('data', resp.data.roomBlocking as IRoomBlocking[]);
            });
        
        // eslint-disable-next-line
    }, [state.values.filters]);

    const handleAddEdit = useCallback((row: IRoomBlocking | undefined) => {
        state.update('selectedRow', row);
        addEditDialog.handleOpen();

        // eslint-disable-next-line
    }, []);

    const handleRemove = useCallback((row: IRoomBlocking) => {
        state.update('selectedRow', row);
        removeDialog.handleOpen();

        // eslint-disable-next-line
    }, []);


    const dataGridColumns = [
        { 
            field: 'roomDescription', 
            headerName: translate('room'),
        }, { 
            field: 'date',  
            headerName: translate('date'),  
            renderCell: (params: any) => {
                return DateTimeHelper.Format(params.row.date, 'DD/MM/YYYY');
            }
        }, { 
            field: 'typeDescription',  
            headerName: translate('blockingType'), 
            flex: 1, 
            minWidth: 120
        }, { 
            field: 'description',  
            headerName: translate('description'), 
            flex: 1, 
            minWidth: 400
        }, { 
            field: 'createdAt',  
            headerName: translate('createdAt'), 
            flex: 1,
            minWidth: 200,
            renderCell: (params: any) => {
                const createdAt = DateTimeHelper.Format(params.row.createdAt, 'DD/MM/YYYY');
                return <Typography className='fontsize-14 lineheight-14'>
                    {createdAt}
                    <Typography className='fontsize-12'>
                        {translate('by').toLowerCase()} 
                        {' '}
                        {params.row.createdBy}
                    </Typography> 
                </Typography>;
            }
        }, { 
            field: 'removedAt',  
            headerName: translate('removedAt'), 
            flex: 1,
            minWidth: 200,
            renderCell: (params: any) => {
                if (!params.row.removedBy) {
                    return null;
                }

                const removedAt = DateTimeHelper.Format(params.row.removedAt, 'DD/MM/YYYY');
                return <Typography className='fontsize-14 lineheight-14 color-red'>
                    {removedAt}
                    <Typography className='fontsize-12'>
                        {translate('by').toLowerCase()} 
                        {' '}
                        {params.row.removedBy}
                    </Typography> 
                </Typography>;
            }
        }, { 
            field: 'actions', 
            type: 'actions', 
            headerName: translate('actions'), 
            width: 200, 
            renderCell: (params: any) => {
                if (Number(params.row.registrationStatusId) === RegistrationStatusEnum.Excluded) {
                    return;
                }

                return (
                    <Grid item>
                        {
                            hasPermission(`${linkProps.alias[0]}.Edit`) && (
                                <Tooltip title={translate('edit')}>
                                    <IconButton className='actionbutton-edit' onClick={() => handleAddEdit(params.row)}>
                                        <Edit fontSize='small' />
                                    </IconButton>
                                </Tooltip>
                            )
                        }

                        {
                            hasPermission(`${linkProps.alias[0]}.Remove`) && (
                                <Tooltip title={translate('remove')}>
                                    <IconButton className='actionbutton-remove' onClick={() => handleRemove(params.row)}>
                                        <Delete fontSize='small' />
                                    </IconButton>
                                </Tooltip>
                            )
                        }
                    </Grid>
                );
            }
        }
    ];

    return (
        <>
            <Grid item xs={12} className='grid-header'>
                <Grid item className='grid-title'>
                    <ContentTitle {...linkProps} />
                </Grid>

                <Grid item className='grid-tools'>
                    {
                        hasPermission(`${linkProps.alias[0]}.Add`) && (
                            <Tooltip title={translate('add')}>
                                <IconButton onClick={() => handleAddEdit(undefined)}>
                                    <Add />
                                </IconButton>
                            </Tooltip>
                        )
                    }
                    
                    <Tooltip title={translate('filter')}>
                        <IconButton onClick={() => filtersDrawer.handleOpen()}>
                            <FilterAlt />
                        </IconButton>
                    </Tooltip>
                    
                    <Tooltip title={translate('refresh')}>
                        <IconButton onClick={handleRefresh}>
                            <Refresh />
                        </IconButton>
                    </Tooltip>
                </Grid>
            </Grid>

            <Grid container className='grid-content'>
                <Grid item xs={12} className='paper-content'>
                    <DataGrid 
                        columns={dataGridColumns} 
                        data={state.values.data} 
                        openSearch={false}
                        onCellDoubleClick={params => {
                            if (Number(params.row.registrationStatusId) === RegistrationStatusEnum.Excluded) {
                                return;
                            }

                            if (hasPermission(`${linkProps.alias[0]}.Edit`)) {
                                handleAddEdit(params.row);
                                return;
                            }
                            
                            if (hasPermission(`${linkProps.alias[0]}.Remove`)) {
                                handleRemove(params.row);
                                return;
                            }
                        }}
                        getRowClassName={(params: any) => {
                            return params.row.removedBy ? 'error' : '';
                        }}
                    />
                </Grid>
            </Grid>

            <Filters
                open={filtersDrawer.open}
                handleClose={() => filtersDrawer.handleClose()}
                handleApplyFilters={(formData) => state.update('filters', formData)}
            />

            <AddEditDialog
                open={addEditDialog.open}
                handleClose={() => addEditDialog.handleClose()}
                data={state.values.selectedRow}
                handleListRefresh={handleRefresh}
            />

            <RemoveDialog
                open={removeDialog.open}
                handleClose={() => removeDialog.handleClose()}
                data={state.values.selectedRow}
                handleListRefresh={handleRefresh}
            />
        </>
    );
}

export default RoomBlocking;